import { useCallback, useEffect, useState } from 'react';

/**
 * Keep a piece of state in sync with a URL search param. Survives page
 * refreshes + back/forward, supports deep-linking. No react-router
 * dependency — uses the History API directly.
 *
 * Usage:
 *   const [view, setView] = useUrlState('view', 'master');
 *   // URL becomes ?view=megaphone when setView('megaphone') runs.
 *
 * Multiple components can call this with different keys — they share
 * the same URL. Setting a value replaces history (no extra entries
 * for tab clicks; back-button still works for "real" navigation).
 */
export function useUrlState(key: string, defaultValue: string): [string, (next: string) => void] {
  // Read initial value from URL (works on first render + after refresh).
  const readFromUrl = (): string => {
    if (typeof window === 'undefined') return defaultValue;
    const v = new URLSearchParams(window.location.search).get(key);
    return v ?? defaultValue;
  };

  const [value, setValue] = useState<string>(readFromUrl);

  // Write to URL when state changes (replaceState — no history spam).
  const setAndPush = useCallback(
    (next: string) => {
      setValue(next);
      if (typeof window === 'undefined') return;
      const url = new URL(window.location.href);
      if (next === defaultValue) {
        url.searchParams.delete(key);
      } else {
        url.searchParams.set(key, next);
      }
      window.history.replaceState({}, '', url.toString());
    },
    [key, defaultValue],
  );

  // Sync state if the URL changes from outside (back/forward).
  useEffect(() => {
    const onPop = () => setValue(readFromUrl());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return [value, setAndPush];
}


/**
 * Number-typed variant — Material Tabs uses numeric indices for the
 * `value` prop, so we serialize/deserialize int.
 */
export function useUrlIntState(key: string, defaultValue: number): [number, (next: number) => void] {
  const [str, setStr] = useUrlState(key, String(defaultValue));
  const parsed = Number.parseInt(str, 10);
  const value = Number.isFinite(parsed) ? parsed : defaultValue;
  const setNum = useCallback((next: number) => setStr(String(next)), [setStr]);
  return [value, setNum];
}
